import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  Ref,
  useState,
  useCallback,
} from "react";

import { Box, IconButton, Slider, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ReactPlayer from "react-player";

import { VideoPlayerState } from "../types/VideoPlayerState";

interface VideoPlayerProps {
  src: string;
  videoPlayerState: VideoPlayerState;
  onUpdatePlayerState: (state: VideoPlayerState) => void;
}

export interface VideoPreviewPlayerHandle {
  seekTo: (time: number) => void;
  play: () => void;
  pause: () => void;
  isPlaying: () => boolean;
}

const fmt = (s: number): string => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const VideoPreviewPlayer = forwardRef<
  VideoPreviewPlayerHandle,
  VideoPlayerProps
>(
  (
    { src, videoPlayerState, onUpdatePlayerState }: VideoPlayerProps,
    ref: Ref<VideoPreviewPlayerHandle>,
  ) => {
    const playerRef = useRef<ReactPlayer>(null);
    const { isPlaying } = videoPlayerState;

    const [duration, setDuration] = useState(0);
    const [played, setPlayed] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [muted, setMuted] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showControls = useCallback(() => {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (isPlaying) {
        hideTimerRef.current = setTimeout(
          () => setControlsVisible(false),
          2500,
        );
      }
    }, [isPlaying]);

    const togglePlay = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: !isPlaying });
      showControls();
    }, [videoPlayerState, isPlaying, onUpdatePlayerState, showControls]);

    const handleProgress = useCallback(
      ({ playedSeconds }: { playedSeconds: number }): void => {
        if (!seeking) {
          setPlayed(playedSeconds);
          onUpdatePlayerState({
            ...videoPlayerState,
            currentTime: playedSeconds,
          });
        }
      },
      [seeking, videoPlayerState, onUpdatePlayerState],
    );

    const handleDuration = useCallback((d: number) => setDuration(d), []);

    const handlePlay = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: true });
    }, [videoPlayerState, onUpdatePlayerState]);

    const handlePause = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
      setControlsVisible(true);
    }, [videoPlayerState, onUpdatePlayerState]);

    const handleEnded = useCallback((): void => {
      onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
      setControlsVisible(true);
    }, [videoPlayerState, onUpdatePlayerState]);

    const handleSliderChange = useCallback(
      (_: Event, value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        setPlayed(v);
        playerRef.current?.seekTo(v, "seconds");
      },
      [],
    );

    const handleSliderMouseDown = useCallback(() => setSeeking(true), []);
    const handleSliderChangeCommitted = useCallback(
      () => setSeeking(false),
      [],
    );

    useImperativeHandle(ref, () => ({
      seekTo: (time: number): void => {
        playerRef.current?.seekTo(time);
        setPlayed(time);
      },
      play: (): void => {
        const ip = playerRef.current?.getInternalPlayer();
        if (ip?.paused) {
          ip.play();
          onUpdatePlayerState({ ...videoPlayerState, isPlaying: true });
        }
      },
      pause: (): void => {
        const ip = playerRef.current?.getInternalPlayer();
        if (ip && !ip.paused) {
          ip.pause();
          onUpdatePlayerState({ ...videoPlayerState, isPlaying: false });
        }
      },
      isPlaying: (): boolean =>
        !playerRef.current?.getInternalPlayer()?.paused || false,
    }));

    return (
      <Box
        onMouseMove={showControls}
        onMouseEnter={showControls}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          bgcolor: "#0a0a0a",
          borderRadius: "12px",
          overflow: "hidden",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {/* ReactPlayer — no native controls */}
        <Box
          onClick={togglePlay}
          sx={{ width: "100%", height: "100%", display: "flex" }}
        >
          <ReactPlayer
            ref={playerRef}
            url={src}
            playing={isPlaying}
            muted={muted}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            width="100%"
            height="100%"
            controls={false}
            style={{ display: "block" }}
            config={{
              file: {
                attributes: { controlsList: "nodownload" },
              },
            }}
          />
        </Box>

        {/* Centered play button — always visible when paused */}
        {!isPlaying && (
          <Box
            onClick={togglePlay}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 20px rgba(37,99,235,0.5)",
                pointerEvents: "auto",
                cursor: "pointer",
                transition: "transform 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: "#1d4ed8",
                  transform: "scale(1.06)",
                },
              }}
            >
              <PlayArrowIcon sx={{ color: "#fff", fontSize: 32, ml: "3px" }} />
            </Box>
          </Box>
        )}

        {/* Bottom controls bar */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)",
            px: 1.5,
            pt: 3,
            pb: 1,
            opacity: controlsVisible || !isPlaying ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        >
          {/* Progress slider */}
          <Slider
            value={played}
            min={0}
            max={duration || 1}
            step={0.1}
            onChange={handleSliderChange}
            onMouseDown={handleSliderMouseDown}
            onChangeCommitted={handleSliderChangeCommitted}
            sx={{
              color: "#2563eb",
              height: 3,
              padding: "6px 0",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                transition: "0.1s",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(37,99,235,0.25)",
                },
              },
              "& .MuiSlider-rail": {
                opacity: 0.35,
                backgroundColor: "#fff",
              },
              "& .MuiSlider-track": {
                border: "none",
              },
            }}
          />

          {/* Controls row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
            }}
          >
            <IconButton
              onClick={togglePlay}
              size="small"
              sx={{ color: "#fff", p: 0.25 }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: 20 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "12px",
                fontVariantNumeric: "tabular-nums",
                flex: 1,
                userSelect: "none",
                pl: 0.5,
              }}
            >
              {fmt(played)} / {fmt(duration)}
            </Typography>

            <IconButton
              onClick={() => setMuted((m) => !m)}
              size="small"
              sx={{ color: "#fff", p: 0.25 }}
            >
              {muted ? (
                <VolumeOffIcon sx={{ fontSize: 18 }} />
              ) : (
                <VolumeUpIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  },
);

VideoPreviewPlayer.displayName = "VideoPreviewPlayer";

export default VideoPreviewPlayer;
